// context/AuthContext.js - Authentication context provider
import React, {createContext, useContext, useEffect, useState} from 'react';
import apiService from '../services/api';

// Create the auth context
export const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({children}) => {
	const [currentUser, setCurrentUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	
	// Check if user is already logged in on mount
	useEffect(() => {
		const checkAuthStatus = async () => {
			try {
				const token = localStorage.getItem('auth_token');
				if (token) {
					const response = await apiService.getCurrentUser();
					
					// Extract the user object from the response
					const user = response.user;
					
					// Retrieve the auth provider from localStorage
					const authProvider = localStorage.getItem('auth_provider') || user.authProvider || 'local';
					
					// Add authProvider to the user object
					const userWithProvider = {
						...user,
						authProvider,
						// Ensure we have a consistent structure for the fullName
						fullName: user.fullName || (user.profile && user.profile.full_name)
					};
					
					setCurrentUser(userWithProvider);
				}
			} catch (err) {
				console.error('Authentication check failed:', err);
				// Clear invalid token
				localStorage.removeItem('auth_token');
				localStorage.removeItem('auth_provider');
			} finally {
				setLoading(false);
			}
		};
		
		checkAuthStatus();
	}, []);
	
	// Register a new user
	const register = async (email, password, fullName) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.register(email, password, fullName);
			// Store the token in localStorage and set the current user
			if (response.token) {
				localStorage.setItem('auth_token', response.token);
				setCurrentUser(response.user);
			}
			return response;
		} catch (err) {
			setError(err.message || 'Registration failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Login user
	const login = async (email, password) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.login(email, password);
			localStorage.setItem('auth_token', response.token);
			
			// Add authProvider information to the user object for regular login
			const userWithProvider = {
				...response.user,
				authProvider: 'local'
			};
			
			// Store the auth provider in localStorage for persistence
			localStorage.setItem('auth_provider', 'local');
			
			setCurrentUser(userWithProvider);
			return response;
		} catch (err) {
			setError(err.message || 'Login failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Logout user
	const logout = async () => {
		setLoading(true);
		try {
			await apiService.logout();
			localStorage.removeItem('auth_token');
			localStorage.removeItem('auth_provider');
			setCurrentUser(null);
		} catch (err) {
			console.error('Logout error:', err);
		} finally {
			setLoading(false);
		}
	};
	
	// Request password reset
	const requestPasswordReset = async (email) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.requestPasswordReset(email);
			return response;
		} catch (err) {
			setError(err.message || 'Password reset request failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Reset password with token
	const resetPassword = async (token, newPassword) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.resetPassword(token, newPassword);
			return response;
		} catch (err) {
			setError(err.message || 'Password reset failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Verify email with token
	const verifyEmail = async (token) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.verifyEmail(token);
			return response;
		} catch (err) {
			setError(err.message || 'Email verification failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Social login
	const socialLogin = async (provider, providerData) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.socialLogin(provider, providerData);
			localStorage.setItem('auth_token', response.token);
			
			// Add authProvider information to the user object
			const userWithProvider = {
				...response.user,
				authProvider: response.user.authProvider || provider
			};
			
			// Store the auth provider in localStorage for persistence
			localStorage.setItem('auth_provider', provider);
			
			setCurrentUser(userWithProvider);
			return response;
		} catch (err) {
			setError(err.message || 'Social login failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Update user profile
	const updateUserProfile = async (profileData) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.updateUserProfile(profileData);
			
			// Get the current authProvider value
			const authProvider = currentUser?.authProvider || localStorage.getItem('auth_provider') || 'local';
			
			// Create a properly structured user object
			const updatedUser = {
				...response.user,
				authProvider,
				fullName: profileData.fullName || response.user.fullName || (response.user.profile && response.user.profile.full_name)
			};
			
			setCurrentUser(updatedUser);
			return response;
		} catch (err) {
			setError(err.message || 'Profile update failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Update password
	const updatePassword = async (currentPassword, newPassword) => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.updatePassword(currentPassword, newPassword);
			return response;
		} catch (err) {
			setError(err.message || 'Password update failed');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Resend verification email
	const resendVerificationEmail = async (email) => {
		setLoading(true);
		setError(null);
		try {
			console.log('resendVerificationEmail called with email:', email);
			console.log('Current user state:', currentUser);
			
			// Try to get the email from different sources with more detailed logging
			let emailToUse = email;
			
			if (!emailToUse && currentUser) {
				console.log('No email provided, attempting to use currentUser.email');
				emailToUse = currentUser.email;
				console.log('Found email from currentUser:', emailToUse);
			}
			
			// Final check if we have a valid email
			if (!emailToUse) {
				console.error('No email available from any source');
				const errorMessage = 'Email is required. Please ensure you are logged in or provide an email.';
				setError(errorMessage);
				throw new Error(errorMessage);
			}
			
			console.log('Proceeding with email:', emailToUse);
			const response = await apiService.resendVerificationEmail(emailToUse);
			console.log('Verification email sent successfully:', response);
			return response;
		} catch (err) {
			console.error('Error in resendVerificationEmail:', err);
			setError(err.message || 'Failed to resend verification email');
			throw err;
		} finally {
			setLoading(false);
		}
	};
	
	// Context value
	const value = {
		currentUser,
		loading,
		error,
		isAuthenticated: !!currentUser,
		register,
		login,
		logout,
		requestPasswordReset,
		resetPassword,
		verifyEmail,
		socialLogin,
		updateUserProfile,
		updatePassword,
		resendVerificationEmail,
	};
	
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
